/** Prerender-safe mount point for the Recovry AI widget. */
import { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { AiLauncher } from './AiLauncher';
import { useAiChat } from './useAiChat';

const AiChatPanel = lazy(() => import('./AiChatPanel'));

// Routes where a floating bubble would sit on top of the primary action.
const HIDDEN_ON = [
  '/login',
  '/signup',
  '/verify-otp',
  '/forgot-password',
  '/complete-profile',
];

function isPrerender(): boolean {
  if (typeof window === 'undefined') return true;
  if ((window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__) return true;
  return Boolean(navigator.webdriver);
}

export function AiChatMount() {
  // Lazy initialiser, not an effect: window.__PRERENDER__ is set before any
  // app JS runs, so the answer is already known at first render.
  const [mounted] = useState(() => !isPrerender());
  const chat = useAiChat();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  // The quota wall sends users to /login with returnTo; ?ai=1 reopens the
  // panel afterwards so the conversation they were mid-way through resumes.
  useEffect(() => {
    if (mounted && params.get('ai') === '1') {
      chat.openChat();
      const next = new URLSearchParams(params);
      next.delete('ai');
      setParams(next, { replace: true });
    }
  }, [mounted, params, chat, setParams]);

  if (!mounted) return null;
  if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <>
      <AiLauncher />
      {chat.open && (
        <Suspense fallback={null}>
          <AiChatPanel />
        </Suspense>
      )}
    </>
  );
}
